import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown } from "lucide-react";

const hoolduskoristusServices = [
  { title: "Äriruumide hoolduskoristus", link: "/hoolduskoristus/kontorid" },
  { title: "KÜ sise- ja välikoristusteenus", link: "/hoolduskoristus/korteriuhistud" },
  { title: "Regulaarne kodukoristus", link: "/hoolduskoristus/kodukoristus" },
  { title: "Trepikodade koristus", link: "/hoolduskoristus/trepikojad" },
];

const valiskoristusedServices = [
  { title: "Fassaadipesu", link: "/valiskoristused/fassaadipesu" },
  { title: "Katuse pesu", link: "/valiskoristused/katuse-pesu" },
  { title: "Lumekoristus katusel", link: "/valiskoristused/lumekoristus-katusel" },
  { title: "Lumekoristus Rakveres", link: "/valiskoristused/lumekoristus-rakveres" },
  { title: "Terrasside puhastus", link: "/valiskoristused/terrasside-puhastus" },
  { title: "Vihmaveerennide puhastus", link: "/valiskoristused/vihmaveerennide-puhastus" },
  { title: "Tänavakivide pesu", link: "/valiskoristused/tanavakivide-pesu" },
];

const haljastusServices = [
  { title: "Muru niitmine", link: "/haljastus/muru-niitmine" },
  { title: "Lehtede koristus ja äravedu", link: "/haljastus/lehtede-koristus" },
  { title: "Jäätmete äravedu", link: "/haljastus/jaatmete-aravedu" },
];

const eripuhastusServices = [
  { title: "Akende pesu", link: "/eripuhastus/akende-pesu" },
  { title: "Vaipkatete süvapesu", link: "/eripuhastus/vaipkatete-suvapesu" },
  { title: "Pehme mööbli puhastus", link: "/eripuhastus/pehme-moobli-puhastus" },
  { title: "Põrandate süvapesu", link: "/eripuhastus/porandate-suvapesu" },
  { title: "Ehitusjärgne koristus", link: "/eripuhastus/ehitusjargne-koristus" },
  { title: "Aurupuhastus", link: "/eripuhastus/aurupuhastus" },
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isHooldusOpen, setIsHooldusOpen] = useState(false);
  const [isValiskoristusedOpen, setIsValiskoristusedOpen] = useState(false);
  const [isHaljastusOpen, setIsHaljastusOpen] = useState(false);
  const [isEripuhastusOpen, setIsEripuhastusOpen] = useState(false);
  const [isMobileHooldusOpen, setIsMobileHooldusOpen] = useState(false);
  const [isMobileValiskoristusedOpen, setIsMobileValiskoristusedOpen] = useState(false);
  const [isMobileHaljastusOpen, setIsMobileHaljastusOpen] = useState(false);
  const [isMobileEripuhastusOpen, setIsMobileEripuhastusOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    if (isHome) {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      window.location.href = `/#${id}`;
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-card/95 backdrop-blur-md shadow-soft border-b border-border/50"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-6">
        <nav className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            className={`text-xl font-serif font-bold transition-colors duration-300 ${
              isScrolled || !isHome ? "text-foreground" : "text-primary-foreground"
            }`}
          >
            LVK Haldus OÜ
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <button
              onClick={() => scrollToSection("services")}
              className={`font-medium transition-colors duration-300 hover:text-accent ${
                isScrolled || !isHome ? "text-foreground" : "text-primary-foreground"
              }`}
            >
              Teenused
            </button>
            
            {/* Hoolduskoristus Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsHooldusOpen(true)}
              onMouseLeave={() => setIsHooldusOpen(false)}
            >
              <Link
                to="/hoolduskoristus"
                className={`font-medium transition-colors duration-300 hover:text-accent flex items-center gap-1 ${
                  isScrolled || !isHome ? "text-foreground" : "text-primary-foreground"
                }`}
              >
                Hoolduskoristus
                <ChevronDown className={`w-4 h-4 transition-transform ${isHooldusOpen ? "rotate-180" : ""}`} />
              </Link>
              
              <AnimatePresence>
                {isHooldusOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-2 w-64 bg-card rounded-xl shadow-lg border border-border/50 overflow-hidden"
                  >
                    {hoolduskoristusServices.map((service) => (
                      <Link
                        key={service.link}
                        to={service.link}
                        className="block px-4 py-3 text-foreground hover:bg-primary/10 hover:text-primary transition-colors text-sm"
                      >
                        {service.title}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Väliskoristused Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsValiskoristusedOpen(true)}
              onMouseLeave={() => setIsValiskoristusedOpen(false)}
            >
              <Link
                to="/valiskoristused"
                className={`font-medium transition-colors duration-300 hover:text-accent flex items-center gap-1 ${
                  isScrolled || !isHome ? "text-foreground" : "text-primary-foreground"
                }`}
              >
                Väliskoristused
                <ChevronDown className={`w-4 h-4 transition-transform ${isValiskoristusedOpen ? "rotate-180" : ""}`} />
              </Link>
              
              <AnimatePresence>
                {isValiskoristusedOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-2 w-64 bg-card rounded-xl shadow-lg border border-border/50 overflow-hidden"
                  >
                    {valiskoristusedServices.map((service) => (
                      <Link
                        key={service.link}
                        to={service.link}
                        className="block px-4 py-3 text-foreground hover:bg-primary/10 hover:text-primary transition-colors text-sm"
                      >
                        {service.title}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Haljastus Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsHaljastusOpen(true)}
              onMouseLeave={() => setIsHaljastusOpen(false)}
            >
              <Link
                to="/haljastus"
                className={`font-medium transition-colors duration-300 hover:text-accent flex items-center gap-1 ${
                  isScrolled || !isHome ? "text-foreground" : "text-primary-foreground"
                }`}
              >
                Haljastus
                <ChevronDown className={`w-4 h-4 transition-transform ${isHaljastusOpen ? "rotate-180" : ""}`} />
              </Link>
              
              <AnimatePresence>
                {isHaljastusOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-2 w-64 bg-card rounded-xl shadow-lg border border-border/50 overflow-hidden"
                  >
                    {haljastusServices.map((service) => (
                      <Link
                        key={service.link}
                        to={service.link}
                        className="block px-4 py-3 text-foreground hover:bg-primary/10 hover:text-primary transition-colors text-sm"
                      >
                        {service.title}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Eripuhastus Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsEripuhastusOpen(true)}
              onMouseLeave={() => setIsEripuhastusOpen(false)}
            >
              <Link
                to="/eripuhastus"
                className={`font-medium transition-colors duration-300 hover:text-accent flex items-center gap-1 ${
                  isScrolled || !isHome ? "text-foreground" : "text-primary-foreground"
                }`}
              >
                Eripuhastus
                <ChevronDown className={`w-4 h-4 transition-transform ${isEripuhastusOpen ? "rotate-180" : ""}`} />
              </Link>
              
              <AnimatePresence>
                {isEripuhastusOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-2 w-64 bg-card rounded-xl shadow-lg border border-border/50 overflow-hidden"
                  >
                    {eripuhastusServices.map((service) => (
                      <Link
                        key={service.link}
                        to={service.link}
                        className="block px-4 py-3 text-foreground hover:bg-primary/10 hover:text-primary transition-colors text-sm"
                      >
                        {service.title}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <Link
              to="/hinnakiri"
              className={`font-medium transition-colors duration-300 hover:text-accent ${
                isScrolled || !isHome ? "text-foreground" : "text-primary-foreground"
              }`}
            >
              Hinnakiri
            </Link>
            <Link
              to="/galerii"
              className={`font-medium transition-colors duration-300 hover:text-accent ${
                isScrolled || !isHome ? "text-foreground" : "text-primary-foreground"
              }`}
            >
              Tehtud tööd
            </Link>
            <button
              onClick={() => scrollToSection("contact")}
              className={`font-medium transition-colors duration-300 hover:text-accent ${
                isScrolled || !isHome ? "text-foreground" : "text-primary-foreground"
              }`}
            >
              Kontakt
            </button>
            <Button
              onClick={() => scrollToSection("contact")}
              className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-6 rounded-full"
            >
              Võta ühendust
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden p-2 transition-colors duration-300 ${
              isScrolled || !isHome ? "text-foreground" : "text-primary-foreground"
            }`}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </nav>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-card rounded-2xl mb-4 p-6 shadow-card border border-border/50"
          >
            <div className="flex flex-col gap-4">
              <button
                onClick={() => scrollToSection("services")}
                className="text-foreground font-medium py-2 hover:text-accent transition-colors text-left"
              >
                Teenused
              </button>
              
              {/* Mobile Hoolduskoristus */}
              <div>
                <button
                  onClick={() => setIsMobileHooldusOpen(!isMobileHooldusOpen)}
                  className="text-foreground font-medium py-2 hover:text-accent transition-colors flex items-center justify-between w-full"
                >
                  Hoolduskoristus
                  <ChevronDown className={`w-4 h-4 transition-transform ${isMobileHooldusOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {isMobileHooldusOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="pl-4 mt-2 border-l-2 border-primary/30"
                    >
                      {hoolduskoristusServices.map((service) => (
                        <Link
                          key={service.link}
                          to={service.link}
                          className="block py-2 text-muted-foreground hover:text-primary transition-colors text-sm"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {service.title}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Mobile Väliskoristused */}
              <div>
                <button
                  onClick={() => setIsMobileValiskoristusedOpen(!isMobileValiskoristusedOpen)}
                  className="text-foreground font-medium py-2 hover:text-accent transition-colors flex items-center justify-between w-full"
                >
                  Väliskoristused
                  <ChevronDown className={`w-4 h-4 transition-transform ${isMobileValiskoristusedOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {isMobileValiskoristusedOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="pl-4 mt-2 border-l-2 border-primary/30"
                    >
                      {valiskoristusedServices.map((service) => (
                        <Link
                          key={service.link}
                          to={service.link}
                          className="block py-2 text-muted-foreground hover:text-primary transition-colors text-sm"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {service.title}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Mobile Haljastus */}
              <div>
                <button
                  onClick={() => setIsMobileHaljastusOpen(!isMobileHaljastusOpen)}
                  className="text-foreground font-medium py-2 hover:text-accent transition-colors flex items-center justify-between w-full"
                >
                  Haljastus
                  <ChevronDown className={`w-4 h-4 transition-transform ${isMobileHaljastusOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {isMobileHaljastusOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="pl-4 mt-2 border-l-2 border-primary/30"
                    >
                      {haljastusServices.map((service) => (
                        <Link
                          key={service.link}
                          to={service.link}
                          className="block py-2 text-muted-foreground hover:text-primary transition-colors text-sm"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {service.title}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Mobile Eripuhastus */}
              <div>
                <button
                  onClick={() => setIsMobileEripuhastusOpen(!isMobileEripuhastusOpen)}
                  className="text-foreground font-medium py-2 hover:text-accent transition-colors flex items-center justify-between w-full"
                >
                  Eripuhastus
                  <ChevronDown className={`w-4 h-4 transition-transform ${isMobileEripuhastusOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {isMobileEripuhastusOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="pl-4 mt-2 border-l-2 border-primary/30"
                    >
                      {eripuhastusServices.map((service) => (
                        <Link
                          key={service.link}
                          to={service.link}
                          className="block py-2 text-muted-foreground hover:text-primary transition-colors text-sm"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {service.title}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <Link
                to="/hinnakiri"
                className="text-foreground font-medium py-2 hover:text-accent transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Hinnakiri
              </Link>
              <Link
                to="/galerii"
                className="text-foreground font-medium py-2 hover:text-accent transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Tehtud tööd
              </Link>
              <button
                onClick={() => scrollToSection("contact")}
                className="text-foreground font-medium py-2 hover:text-accent transition-colors text-left"
              >
                Kontakt
              </button>
              <Button
                onClick={() => scrollToSection("contact")}
                className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold rounded-full mt-2"
              >
                Võta ühendust
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </motion.header>
  );
};

export default Navbar;
